import React, { useState, useEffect, useRef } from "react";
import { saveEmail } from "../utils/emailCollection";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/components/AfterResultBot.css";
import "../styles/components/LandingPage.css";
import omnicareLogo from "../assets/icons/logo.svg";
import coffeeIcon from "../assets/icons/coffee.svg";
import starsIcon from "../assets/icons/stars.svg";
import stethoscopeIcon from "../assets/icons/stethoscope.svg";
import landingPageNumber1 from "../assets/icons/landing_page_number_1.svg";
import landingPageCoffee from "../assets/icons/landing_page_coffee.svg";
import landingPageNumber2 from "../assets/icons/landing_page_number_2.svg";
import landingPageNumber3 from "../assets/icons/landing_page_number_3.svg";
import landingPageStar from "../assets/icons/landing_page_star.svg";
import landingPageStrong from "../assets/icons/landing_page_strong.svg";
import healthTipsIcon from "../assets/icons/health_tips.svg";
import LanguageSwitcher from "./LanguageSwitcher";
import { getVisitorCount, incrementVisitorCount } from "../utils/visitorCounter";

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [visitorCount, setVisitorCount] = useState(null);
  const [subscribeEmail, setSubscribeEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState(""); // "" | "sending" | "success" | "error"
  const emailInputRef = useRef(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subscribeEmail)) {
      if (emailInputRef.current) emailInputRef.current.focus();
      return;
    }
    setSubscribeStatus("sending");
    try {
      await saveEmail(subscribeEmail, { locale: t("common.lang") || "en", source: "landing" });
      setSubscribeStatus("success");
      setSubscribeEmail("");
    } catch {
      setSubscribeStatus("error");
    }
  };

  useEffect(() => {
    // Fetch count from Firebase, then increment
    getVisitorCount().then((count) => {
      setVisitorCount(count);
      incrementVisitorCount();
    });
  }, []);

  const handleLetsChat = () => {
    navigate("/survey/1");
  };

  const handleHealthTips = () => {
    navigate("/health-tips");
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="after-result-bot-header">
        <div className="logo-section">
          <div className="logo">
            <img src={omnicareLogo} alt="Pebble Health Logo" className="logo-icon" />
            <span className="logo-text">Pebble Health</span>
          </div>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="health-tips-link"
            onClick={handleHealthTips}
            aria-label={t('common.viewHealthTips')}
          >
            <img src={healthTipsIcon} alt="" className="health-tips-link-icon" aria-hidden="true" />
            <span>{t('common.viewHealthTips')}</span>
          </button>
          <a
            href="mailto:omnicareai@gmail.com"
            className="contact-us-link"
            aria-label={t('common.contactUs', 'Contact Us')}
          >
            <span>{t('common.contactUs', 'Contact Us')}</span>
          </a>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Main Content */}
      <div className="landing-content">
        {/* Left Section */}
        <div className="left-section">
          {/* Primary Action Button - Top */}
          <div className="primary-action-container">
            <button className="lets-chat-button primary-button" onClick={handleLetsChat}>
              <span>{t('common.takeQuizLine1')}</span>
              
              <span className="quiz-button-subtitle">{t('common.takeQuizLine2')}</span>
            </button>
          </div>

          <h1 className="main-headline">
            {t('landing.title')}
          </h1>

          {/* Landing video (crop top); place file at public/omnicare_landing.mp4 */}
          <div className="illustration">
            <video
              className="illustration-image illustration-video"
              src="/omnicare_landing.mp4"
              autoPlay
              muted
              loop
              playsInline
              aria-label="Pebble Health landing"
            />
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="lets-chat-button" onClick={handleLetsChat}>
              <span>{t('common.takeQuizLine1')}</span>
              
              <span className="quiz-button-subtitle">{t('common.takeQuizLine2')}</span>
            </button>
          </div>

          {/* Visitor Counter */}
          {visitorCount && (
            <p className="visitor-counter">
              {visitorCount.toLocaleString()}+ {t('landing.peopleTested', 'people have taken the assessment')}
            </p>
          )}
        </div>

        {/* Right Section - Three Steps (Desktop) */}
        <div className="right-section">
          {/* Step 1 */}
          <div className="step-card step-card-1">
            <div className="step-header">
              <img
                src={landingPageNumber1}
                alt="1"
                className="step-number-svg"
              />
              <img
                src={landingPageCoffee}
                alt="Coffee"
                className="step-coffee-svg"
              />
              <h3 className="step-title">{t('landing.step1.title')}</h3>
            </div>
            <p className="step-description">
              {t('landing.step1.description')}
            </p>
          </div>

          {/* Step 2 */}
          <div className="step-card step-card-2">
            <div className="step-header">
              <img
                src={landingPageNumber2}
                alt="2"
                className="step-number-svg"
              />
              <img src={landingPageStar} alt="Star" className="step-star-svg" />
              <h3 className="step-title">{t('landing.step2.title')}</h3>
            </div>
            <p className="step-description">
              {t('landing.step2.description')}
            </p>
          </div>

          {/* Step 3 */}
          <div className="step-card step-card-3">
            <div className="step-header">
              <img
                src={landingPageNumber3}
                alt="3"
                className="step-number-svg"
              />
              <img
                src={landingPageStrong}
                alt="Strong"
                className="step-strong-svg"
              />
              <h3 className="step-title">{t('landing.step3.title')}</h3>
            </div>
            <p className="step-description">
              {t('landing.step3.description')}
            </p>
          </div>
        </div>

        {/* Mobile Steps Section */}
        <div className="mobile-steps-section">
          <h2 className="mobile-steps-title">{t('landing.howItWorks')}</h2>
          
          {/* Step 1 - Mobile */}
          <div className="mobile-step-card mobile-step-card-1">
            <div className="mobile-step-header">
              <img
                src={landingPageNumber1}
                alt="1"
                className="mobile-step-number-svg"
              />
              <img
                src={landingPageCoffee}
                alt="Coffee"
                className="mobile-step-icon-svg"
              />
              <h3 className="mobile-step-title">{t('landing.step1.title')}</h3>
            </div>
            <p className="mobile-step-description">
              {t('landing.step1.description')}
            </p>
          </div>

          {/* Step 2 - Mobile */}
          <div className="mobile-step-card mobile-step-card-2">
            <div className="mobile-step-header">
              <img
                src={landingPageNumber2}
                alt="2"
                className="mobile-step-number-svg"
              />
              <img src={landingPageStar} alt="Star" className="mobile-step-icon-svg" />
              <h3 className="mobile-step-title">{t('landing.step2.title')}</h3>
            </div>
            <p className="mobile-step-description">
              {t('landing.step2.description')}
            </p>
          </div>

          {/* Step 3 - Mobile */}
          <div className="mobile-step-card mobile-step-card-3">
            <div className="mobile-step-header">
              <img
                src={landingPageNumber3}
                alt="3"
                className="mobile-step-number-svg"
              />
              <img
                src={landingPageStrong}
                alt="Strong"
                className="mobile-step-icon-svg"
              />
              <h3 className="mobile-step-title">{t('landing.step3.title')}</h3>
            </div>
            <p className="mobile-step-description">
              {t('landing.step3.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Newsletter Subscribe */}
      <div className="subscribe-section">
        <p className="subscribe-title">{t('landing.subscribeTitle', 'Stay Up to Date on Your Health')}</p>
        <p className="subscribe-subtitle">{t('landing.subscribeSubtitle', 'Evidence-based screening reminders and health insights. No spam — unsubscribe anytime.')}</p>
        {subscribeStatus === "success" ? (
          <p className="subscribe-success">✓ {t('landing.subscribeSuccess', "You're subscribed! Check your inbox soon.")}</p>
        ) : (
          <form className="subscribe-form" onSubmit={handleSubscribe}>
            <input
              ref={emailInputRef}
              type="email"
              className="subscribe-input"
              placeholder={t('landing.subscribePlaceholder', 'Enter your email')}
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              disabled={subscribeStatus === "sending"}
            />
            <button type="submit" className="subscribe-button" disabled={subscribeStatus === "sending"}>
              {subscribeStatus === "sending" ? "..." : t('landing.subscribeButton', 'Subscribe')}
            </button>
          </form>
        )}
        {subscribeStatus === "error" && (
          <p className="subscribe-error">{t('landing.subscribeError', 'Something went wrong. Please try again.')}</p>
        )}
      </div>

      {/* Disclaimer */}
      <p className="landing-disclaimer">
        {t('landing.disclaimer', 'Our recommendations are based on published clinical guidelines from USPSTF, ACS, and NCCN. This tool is for informational purposes only and does not replace professional medical advice. Please consult your doctor.')}{' '}
        <span className="landing-disclaimer-link" onClick={() => navigate('/privacy')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
          {t('landing.privacyLink', 'Privacy Policy')}
        </span>
      </p>
    </div>
  );
};

export default LandingPage;
